/**
 * ProfilePage.jsx — Tenant User: My Profile
 * Accessible at /profile — all authenticated users.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getMyProfile, updateMyProfile, changePassword } from '../utils/api';
import './TenantAdminPage.css';

export default function ProfilePage() {
  const { user, token, updateUser } = useAuth();
  const navigate        = useNavigate();

  // Profile form state
  const [profile, setProfile]   = useState(null);
  const [form, setForm]         = useState({ name: '', default_company_code: '', default_plant: '' });
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form state
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwMsg, setPwMsg]   = useState({ type: '', text: '' });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    getMyProfile(token)
      .then(p => {
        setProfile(p);
        setForm({
          name:                 p.name || '',
          default_company_code: p.default_company_code || '',
          default_plant:        p.default_plant || '',
        });
      })
      .catch(err => setProfileMsg({ type: 'error', text: err.message }));
  }, [token]);

  async function handleProfileSave(e) {
    e.preventDefault();
    setProfileMsg({ type: '', text: '' });
    setSavingProfile(true);
    try {
      const updated = await updateMyProfile(token, {
        name:                 form.name || undefined,
        default_company_code: form.default_company_code || undefined,
        default_plant:        form.default_plant || undefined,
      });
      setProfile(updated);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message });
    } finally {
      setSavingProfile(false);
    }
  }

  async function handlePasswordSave(e) {
    e.preventDefault();
    setPwMsg({ type: '', text: '' });
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (pwForm.new_password.length < 6) {
      setPwMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    setSavingPw(true);
    try {
      await changePassword(token, pwForm.current_password, pwForm.new_password);
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      // Clear force-reset flag if it was set (user changed password successfully)
      if (user?.must_change_password) updateUser({ must_change_password: false });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message });
    } finally {
      setSavingPw(false);
    }
  }

  return (
    <div className="tenant-page">
      <div className="tenant-page-header">
        <button className="tenant-back-btn" onClick={() => navigate('/')}>
          <ArrowLeft size={15} /> Back to Chat
        </button>
        <h1 className="tenant-page-title">My Profile</h1>
        <p className="tenant-page-sub">{user?.tenant_id}</p>
      </div>

      <div className="tenant-page-body">

        {/* ── Profile card ── */}
        {profile && (
          <div className="tenant-section">
            <div className="tenant-profile-header">
              <div className="tenant-avatar">
                {profile.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="tenant-profile-meta">
                <div className="tenant-profile-name">{profile.name}</div>
                <div className="tenant-profile-detail">{profile.email}</div>
                <div className="tenant-profile-detail">
                  <span className="tenant-tag">{profile.role}</span>
                  &nbsp;·&nbsp;
                  {profile.last_login
                    ? `Last login: ${new Date(profile.last_login).toLocaleString('en-IN')}`
                    : 'Never logged in'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Edit profile ── */}
        <div className="tenant-section">
          <h2 className="tenant-section-title">Edit Profile</h2>
          <form onSubmit={handleProfileSave} className="tenant-form">
            <div className="tenant-form-row">
              <label>
                Display Name *
                <input
                  className="tenant-input"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label>
                Email
                <input
                  className="tenant-input"
                  value={profile?.email || ''}
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                />
              </label>
            </div>
            <div className="tenant-form-row">
              <label>
                Default Company Code
                <input
                  className="tenant-input"
                  value={form.default_company_code}
                  onChange={e => setForm(f => ({ ...f, default_company_code: e.target.value }))}
                  placeholder="e.g. 1000"
                />
              </label>
              <label>
                Default Plant
                <input
                  className="tenant-input"
                  value={form.default_plant}
                  onChange={e => setForm(f => ({ ...f, default_plant: e.target.value }))}
                  placeholder="e.g. 1010"
                />
              </label>
            </div>
            {profileMsg.text && (
              <p className={profileMsg.type === 'error' ? 'tenant-error' : 'tenant-success'}>
                {profileMsg.text}
              </p>
            )}
            <div className="tenant-form-actions">
              <button type="submit" className="tenant-btn-primary" disabled={savingProfile}>
                <Save size={14} />
                {savingProfile ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        {/* ── Change password ── */}
        <div className="tenant-section">
          <h2 className="tenant-section-title">Change Password</h2>
          <form onSubmit={handlePasswordSave} className="tenant-form">
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.78rem', fontWeight: 600, color: 'var(--clr-text-secondary)' }}>
              Current Password *
              <input
                type="password"
                className="tenant-input"
                value={pwForm.current_password}
                onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
                required
              />
            </label>
            <div className="tenant-form-row">
              <label>
                New Password *
                <input
                  type="password"
                  className="tenant-input"
                  value={pwForm.new_password}
                  minLength={6}
                  onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))}
                  required
                />
              </label>
              <label>
                Confirm New Password *
                <input
                  type="password"
                  className="tenant-input"
                  value={pwForm.confirm_password}
                  onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))}
                  required
                />
              </label>
            </div>
            {pwMsg.text && (
              <p className={pwMsg.type === 'error' ? 'tenant-error' : 'tenant-success'}>
                {pwMsg.text}
              </p>
            )}
            <div className="tenant-form-actions">
              <button type="submit" className="tenant-btn-primary" disabled={savingPw}>
                <Save size={14} />
                {savingPw ? 'Saving…' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}
